<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { ref, watch } from 'vue'
	import { useRoute, useRouter } from 'vue-router'

	import CoincidencesAlert from '@/components/CoincidencesAlert.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import ImageCropper from '@/components/ImageCropper.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledDropZone from '@/components/StyledDropZone.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import CoincidencesList from '@/internal/modules/providers/components/CoincidencesList.vue'
	import { Brand } from '@/internal/modules/providers/interfaces/Brand'

	import useSearchChains from '../../chain/composables/useSearchChains'
	import useBrandForm from '../composables/useBrandForm'
	import useSearchBrands from '../composables/useSearchBrands'

	interface Props {
		brand: Brand
	}

	const route = useRoute()

	const props = defineProps<Props>()
	const queryClient = useQueryClient()

	const {
		handleEdit,
		form,
		isModifying,
		isCoincidence,
		handleEvaluationEdit,
		createMediaMutationLoading,
		actionEdit,
		updateMutationLoading,
		brands: brandsCoincidences,
		createMediaMutationAsync,
	} = useBrandForm(props.brand)

	const { data, isLoading, debouncedSetQuery } = useSearchChains({
		immediate: true,
		errorHandling: false,
	})

	// Image Cropper state
	const showCropper = ref(false)
	const imageToCrop = ref<string>('')
	const tempImageFile = ref<File | null>(null)

	// Watch para detectar cambios en form.logo
	watch(
		() => form.logo,
		(newLogo) => {
			if (
				newLogo &&
				newLogo instanceof File &&
				newLogo !== tempImageFile.value
			) {
				tempImageFile.value = newLogo
				const reader = new FileReader()
				reader.onload = (e) => {
					imageToCrop.value = e.target?.result as string
					showCropper.value = true
				}
				reader.readAsDataURL(newLogo)
			}
		}
	)

	// Manejar cuando se aplica el recorte
	const handleCropApplied = async (croppedFile: File) => {
		tempImageFile.value = croppedFile
		form.logo = croppedFile
		showCropper.value = false

		// Hacer la petición automáticamente después de aplicar el recorte
		await createMediaMutationAsync({
			id: route.params.id as string,
			media: form.logo! as File,
			collection: 'logo',
		})

		await queryClient.invalidateQueries(['brand'])

		isModifying.value = false
	}

	// Manejar cuando se cancela el recorte
	const handleCropCancelled = () => {
		showCropper.value = false
		form.logo = null
		tempImageFile.value = null
	}
</script>

<template>
	<ShadowBox class="bg-white">
		<form @submit.prevent="handleEvaluationEdit">
			<HasPermission
				:name-permission="'create-medias-internal'"
				:entity-type="'App\\Models\\Brand'"
			>
				<p v-if="!isModifying">Imagen del logo {{ brand.name }}</p>
				<img
					:src="brand.logo"
					class="w-24 h-24 rounded-full mb-2 mx-auto"
					:alt="`Imagen de logo ${form.name}`"
					v-if="!isModifying"
				/>

				<button
					class="text-blue-500 flex items-center w-full justify-center gap-2 hover:bg-white hover:text-blue-700"
					@click="
						() => {
							isModifying = true
						}
					"
					type="button"
					v-if="!isModifying"
				>
					Modificar
				</button>

				<StyledDropZone
					v-if="isModifying"
					v-model="form.logo"
					class="!w-[21.375 rem]"
					:accept-types="['image/png', 'image/jpg', 'image/jpeg']"
				/>
			</HasPermission>

			<!-- Image Cropper Modal -->
			<ImageCropper
				v-model:isVisible="showCropper"
				:imageSrc="imageToCrop"
				cropMode="square"
				:aspectRatioLocked="true"
				:uploading="updateMutationLoading || createMediaMutationLoading"
				@crop-applied="handleCropApplied"
				@crop-cancelled="handleCropCancelled"
			/>

			<HasPermission
				:name-permission="'viewAny-internal'"
				:entity-type="'App\\Models\\Chain'"
			>
				<StyledCombobox
					v-model="form.chain"
					placeholder="Buscar cadena"
					class="flex-1 mb-3"
					:options="data?.data || []"
					:loading-options="isLoading"
					:on-input-change="debouncedSetQuery"
					:show-input="!form.chain"
					:default-value="form.chain"
					:is-required="true"
					label="Nombre de la cadena"
				>
					<template #default="{ option }">
						<span>{{ option.name }}</span>
					</template>

					<template #input-overlap>
						<template v-if="form.chain">
							<span>{{ form.chain.name }}</span>

							<button
								class="material-symbols-outlined text-[22px]"
								@click="form.chain = undefined"
							>
								close
							</button>
						</template>
					</template>
				</StyledCombobox>
			</HasPermission>

			<HasPermission
				:name-permission="'update-internal'"
				:entity-type="'App\\Models\\Brand'"
			>
				<StyledInput
					v-model="form.name"
					label="Nombre de la marca"
					placeholder="Nombre de la marca"
					class="flex-1 mb-3"
					:is-required="true"
					:disabled="isCoincidence"
				/>
			</HasPermission>

			<StyledButton
				v-if="!isCoincidence"
				type="submit"
				class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
				:loading="updateMutationLoading || createMediaMutationLoading"
			>
				Editar marca
			</StyledButton>

			<HasPermission
				:name-permission="'update-internal'"
				:entity-type="'App\\Models\\Brand'"
			>
				<CoincidencesList
					:data="brandsCoincidences?.data || []"
					:is-coincidence="isCoincidence"
					:handle-action="handleEdit"
					:clear-state="
						() => {
							isCoincidence = false
						}
					"
					:action-modal="actionEdit"
					:loading-creating="
						updateMutationLoading || createMediaMutationLoading
					"
				/>
			</HasPermission>
		</form>
	</ShadowBox>
</template>
